import React, { useState } from "react";
import "./DownloadSheets.css";
import {
  Activity,
  BarChart2,
  ClipboardList,
  Clock,
  Database,
  Edit3,
  PackageCheck,
  PieChart,
  Settings,
  Target,
  Timer,
  TrendingDown,
  User,
} from "lucide-react";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import SheetModal from "./SheetModal";
import BdmInsights from "../../page-components/ReportAndInsights.js/BdmInsights";
import OperationdataInsights from "../../page-components/ReportAndInsights.js/OperationdataInsights";
import usePermissions from "../../hooks/usePermissions";
import SubscriberDeduction from "../../page-components/ReportAndInsights.js/SubscriberDeductions";
import PerformanceOverview from "../../page-components/ReportAndInsights.js/PerformanceOverview";
import UnderpricedOrders from "../../page-components/ReportAndInsights.js/UnderpricedOrders";
import UnsettledOrders from "../../page-components/ReportAndInsights.js/UnsettledOrders";
import SubscriberOperational from "../../page-components/ReportAndInsights.js/SubscriberOperational";
import ManuallyAssignedOrders from "../../page-components/ReportAndInsights.js/ManuallyAssignedOrders";
import AcceptRejectOrdSheet from "../../page-components/ReportAndInsights.js/AcceptRejectOrdSheet";
import FLOrdersSummary from "../../page-components/ReportAndInsights.js/FLOrdersSummary";
import DownloadAnyFLSheet from "../../page-components/ReportAndInsights.js/DownloadAnyFLSheet";
import UnassignedPendingOrders from "../../page-components/ReportAndInsights.js/UnassignedPendingOrders";
import CompletedOrders from "../../page-components/ReportAndInsights.js/CompletedOrders";
import TimeDifferenceOrder from "../../page-components/ReportAndInsights.js/TimeDifferenceOrder";
import SupportTeamReport from "../../page-components/ReportAndInsights.js/SupportTeamReport";

const dummy_sheet = [
  {
    id: "bdm",
    title: "BDM Insights",
    subtitle: "All orders, transactions & refunds by user in selected period.",
    icon: BarChart2,
    permissions_given: ["can_download_bdm_sheet", "can_download_any_bdm_sheet"],
  },
  {
    id: "ops-sheet",
    title: "Operations Data",
    subtitle: "Key order metrics for operations tracking in selected period.",
    icon: Settings,
    permissions_given: ["can_download_ops_sheet", "can_download_any_ops_sheet"],
  },
  {
    id: "sub-deduction",
    title: "Subscriber Deductions",
    subtitle: "Deductions made from subscriber payouts.",
    icon: PieChart,
    permissions_given: ["can_download_subscriber_deduction_sheet"],
  },
  {
    id: "fl_orders_summary",
    title: "FL Orders Summary",
    subtitle: "Freelancer-wise order and payout data.",
    icon: ClipboardList,
    permissions_given: [
      "can_download_daily_fl_order_sheet_deadline_wise",
      "can_download_daily_fl_order_sheet_start_date_wise",
      "can_download_any_fl_sheet",
    ],
  },
  {
    id: "ar_decisions_summary",
    title: "A/R Decisions Summary",
    subtitle: "Log of accept/reject actions on orders.",
    icon: Target,
    permissions_given: [
      "can_download_all_accept_reject_sheet",
      "can_download_specific_accept_reject_sheet",
      "can_download_manager_accept_reject_sheet",
    ],
  },
  {
    id: "performance_overview",
    title: "Performance Overview",
    subtitle: "Key metrics across teams and orders.",
    icon: Activity,
    permissions_given: ["can_download_performance_overview_sheet"],
  },
  {
    id: "manually_assigned_orders",
    title: "Manually Assigned Orders",
    subtitle: "Orders manually assigned by ops team",
    icon: Edit3,
    permissions_given: ["can_download_manually_assigned_orders_sheet"],
  },
  {
    id: "underpriced-orders",
    title: "Underpriced Orders",
    subtitle: "Orders priced below set benchmarks.",
    icon: TrendingDown,
    permissions_given: ["can_download_underpriced_orders_sheet"],
  },
  {
    id: "unsettled-orders",
    title: "Unsettled Delivered Orders",
    subtitle: "Delivered but unpaid orders list",
    icon: Timer,
    permissions_given: ["can_download_unsettled_orders_sheet"],
  },
  {
    id: "subscriber-opetational",
    title: "Subscriber Operational Data",
    subtitle: "All orders, transactions & refunds by user in selected period.",
    icon: Database,
    permissions_given: ["can_download_subscriber_operational_sheet"],
  },
  {
    id: "unassigned_pending_orders",
    title: "Unassigned & Pending Orders",
    subtitle: "Orders yet to be assigned or with unresolved payment status.",
    icon: Clock,
    permissions_given: ["can_download_unassigned_order_sheet"],
  },
  {
    id: "delivered_closed_orders",
    title: "Delivered & Closed Orders",
    subtitle: "Orders marked as delivered or completed in the workflow.",
    icon: PackageCheck,
    permissions_given: ["can_download_completed_order_sheet"],
  },
  {
    id: "time_difference_orders",
    title: "Time Difference Orders",
    subtitle: "Download CSV for time difference orders within a date range.",
    icon: Clock,
    permissions_given: ["can_view_time_diff_sheet"],
  },
  {
    id: "support_team_report",
    title: "Support Team Orders",
    subtitle: "Orders by Chat Executives & support team members in a selected date range.",
    icon: User,
    permissions_given: ["can_download_support_team_report"],
    info: "Support team members:\nAnkita Aeron\nArpit Aeron\nGaurav Bhardwaj\nAakansha Jhanwar\nGabriela Florentina\n(plus all Chat Executives)",
  },
];

function ReportsInsights() {
  const [open, setOpen] = useState(false);
  const [activeSheet, setActiveSheet] = useState(null);
  const { hasPermission } = usePermissions();

  const handleOpen = (sheet) => {
    setActiveSheet(sheet);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setActiveSheet(null);
  };

  return (
    <div className="download-sheets-container">
      <p style={{ color: "#757575", fontSize: 18, marginBottom: "30px" }}>
        Click on any card below to track performance and activity through
        downloadable reports
      </p>
      <div className="sheets-list">
        {dummy_sheet
          .filter((sheet) => {
            // If no permissions defined, allow by default
            if (!sheet.permissions_given) return true;

            // If at least one permission matches, allow
            return sheet.permissions_given.some((perm) => hasPermission(perm));
          })
          .map((sheet) => {
            const Icon = sheet.icon;
            return (
              <div
                key={sheet.id}
                className="custom-report-tile"
                onClick={() => handleOpen(sheet)}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 20,
                      color: "#18181b",
                      marginBottom: 12,
                      fontFamily: "Inter, sans-serif",
                      letterSpacing: "-0.5px",
                      textAlign: "left",
                    }}
                  >
                    {sheet.title}
                    {sheet.info && (
                      <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{sheet.info}</div>}>
                        <InfoCircleOutlined
                          style={{ marginLeft: 8, cursor: "pointer", color: "#555" }}
                        />
                      </Tooltip>
                    )}
                  </div>
                  <div
                    style={{
                      height: 2,
                      width: "95%",
                      background: "gray",
                      borderRadius: 2,
                      marginBottom: 10,
                    }}
                  />
                  <div
                    style={{
                      fontSize: 14,
                      color: "#222",
                      fontWeight: 400,
                      fontFamily: "Inter, sans-serif",
                      marginBottom: 0,
                      textAlign: "left",
                    }}
                  >
                    {sheet.subtitle}
                  </div>
                </div>
                <div>
                  <Icon size={50} color="#36c6d3" strokeWidth={1.5} />
                </div>
              </div>
            );
          })}
      </div>

      {open && activeSheet && (
        <SheetModal activeSheet={activeSheet} onClose={handleClose}>
          {activeSheet.id === "bdm" && (
            <BdmInsights handleClose={handleClose} />
          )}
          {activeSheet.id === "ops-sheet" && (
            <OperationdataInsights handleClose={handleClose} />
          )}
          {activeSheet.id === "sub-deduction" && (
            <SubscriberDeduction handleClose={handleClose} />
          )}
          {activeSheet.id === "performance_overview" && (
            <PerformanceOverview handleClose={handleClose} />
          )}
          {activeSheet.id === "underpriced-orders" && (
            <UnderpricedOrders handleClose={handleClose} />
          )}
          {activeSheet.id === "unsettled-orders" && (
            <UnsettledOrders handleClose={handleClose} />
          )}
          {activeSheet.id === "subscriber-opetational" && (
            <SubscriberOperational handleClose={handleClose} />
          )}
          {activeSheet.id === "manually_assigned_orders" && (
            <ManuallyAssignedOrders handleClose={handleClose} />
          )}
          {activeSheet.id === "ar_decisions_summary" && (
            <AcceptRejectOrdSheet handleClose={handleClose} />
          )}
          {activeSheet.id === "fl_orders_summary" && (
            <FLOrdersSummary handleClose={handleClose} />
          )}
          {activeSheet.id === "download_any_fl_sheet" && (
            <DownloadAnyFLSheet handleClose={handleClose} />
          )}
          {activeSheet.id === "unassigned_pending_orders" && (
            <UnassignedPendingOrders handleClose={handleClose} />
          )}
          {activeSheet.id === "delivered_closed_orders" && (
            <CompletedOrders handleClose={handleClose} />
          )}
          {activeSheet.id === "time_difference_orders" && (
            <TimeDifferenceOrder handleClose={handleClose} />
          )}
          {activeSheet.id === "support_team_report" && (
            <SupportTeamReport handleClose={handleClose} />
          )}
        </SheetModal>
      )}
    </div>
  );
}

export default ReportsInsights;
